"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Search } from "lucide-react";
import type { HelpSearchEntry } from "@/lib/help";

/**
 * Client-side search over the KB index (issue #1234). The index is small
 * (dozens of articles), so simple substring scoring beats shipping a search
 * vendor. Results replace the category grid while a query is active.
 */
export default function HelpSearch({
  index,
  children,
}: {
  index: HelpSearchEntry[];
  children: React.ReactNode;
}) {
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const terms = q.split(/\s+/);
    return index
      .map((entry) => {
        let score = 0;
        for (const term of terms) {
          if (entry.title.toLowerCase().includes(term)) score += 3;
          else if (entry.haystack.includes(term)) score += 1;
        }
        return { entry, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.entry);
  }, [query, index]);

  return (
    <>
      <div className="relative mx-auto mt-8 max-w-md">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the help centre…"
          aria-label="Search the help centre"
          className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {results === null ? (
        children
      ) : results.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500">
            No articles match. Try different words, or{" "}
            <a
              href="mailto:hello@thesitebook.co.uk"
              className="text-brand-700 underline hover:text-accent-dark"
            >
              email us
            </a>
            .
          </p>
        </div>
      ) : (
        <div className="mt-10 space-y-3">
          {results.map((entry) => (
            <Link
              key={entry.href}
              href={entry.href}
              className="block rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-accent/50"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                {entry.categoryLabel}
              </p>
              <h3 className="mt-1 font-heading text-base font-bold text-gray-900">
                {entry.title}
              </h3>
              <p className="mt-1 text-sm text-gray-600">{entry.description}</p>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
